import { Controller } from '@hotwired/stimulus';
import consumer from "../channels/consumer";
import flash from '../lib/flashes';

export default class extends Controller {
  static values = {
    companySlug: String,
    companyId: Number,
  };

  connect() {
    this.channel = consumer.subscriptions.create(
      { channel: "RadarSyncChannel", company_id: this.companyIdValue },
      {
        received: this.handleReceived.bind(this),
        connected: () => {},
        disconnected: () => {},
      }
    );

    if (this.mainContentNeedsPolling()) {
      this.startMainContentPolling();
    }
  }

  mainContentNeedsPolling() {
    return document.getElementById("radar-main-content")?.hasAttribute("data-radar-polling-needed");
  }

  startMainContentPolling() {
    if (this.pollingInterval) return;
    this.pollingInterval = setInterval(() => {
      this.fetchNetworkScansStatus();
    }, 2 * 60 * 1000);
  }

  stopMainContentPolling() {
    clearInterval(this.pollingInterval);
    this.pollingInterval = null;
  }

  handleReceived(data) {
    if (data.message === "finished") {
      this.fetchSyncStatus();
      this.reloadDevicesList();
    } else if (data.message === "error") {
      this.fetchSyncStatus();
      this.reloadDevicesList();
      flash("Hudu Radar sync finished with errors. Some devices may not be up to date.", "error");
    }
  }

  reloadDevicesList() {
    const devicesFrame = document.querySelector("turbo-frame#devices_list");
    if (!devicesFrame) return;

    const selectedDevice = devicesFrame.querySelector(".radar-interface__device-item--selected");
    const url = new URL(devicesFrame.src, window.location.origin);

    if (selectedDevice) {
      const deviceId = selectedDevice.id.replace("device_list_item_", "");
      url.searchParams.set("selected_device_id", deviceId);
      this.reloadDevicePanel(selectedDevice.href);
    }

    devicesFrame.src = url.toString();
  }

  async reloadDevicePanel(deviceUrl) {
    const devicePanel = document.querySelector("turbo-frame#device_panel");
    if (!devicePanel) return;

    try {
      const response = await fetch(deviceUrl, {
        headers: {
          "Accept": "text/html",
          "Turbo-Frame": "device_panel",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newFrame = doc.querySelector("turbo-frame#device_panel");
      if (!newFrame) return;

      const nextDeviceId = newFrame.getAttribute("data-current-device-id");
      if (nextDeviceId !== null) {
        devicePanel.setAttribute("data-current-device-id", nextDeviceId);
      } else {
        devicePanel.removeAttribute("data-current-device-id");
      }

      const newHeader = newFrame.querySelector("#device_details_header");
      const liveHeader = devicePanel.querySelector("#device_details_header");
      if (newHeader && liveHeader) {
        liveHeader.replaceWith(newHeader);
        window.initializeTippy?.(devicePanel.querySelector("#device_details_header"));
      } else if (newHeader && !liveHeader) {
        devicePanel.innerHTML = newFrame.innerHTML;
        window.initializeTippy?.(devicePanel);
      }
    } catch {
      // Silently fail -- device details will refresh on next user interaction
    }
  }

  fetchSyncStatus() {
    const path = `/c/${this.companySlugValue}/radar_interfaces/sync_now_status`;
    fetch(path, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
        if (this.mainContentNeedsPolling() && !this.pollingInterval) {
          this.startMainContentPolling();
        }
      });
  }

  fetchNetworkScansStatus() {
    const path = `/c/${this.companySlugValue}/radar_interfaces/network_scans_status${window.location.search}`;
    fetch(path, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
        if (!this.mainContentNeedsPolling()) {
          this.stopMainContentPolling();
        }
      });
  }

  disconnect() {
    this.stopMainContentPolling();
    if (this.channel) {
      this.channel.unsubscribe();
    }
  }
}
